import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';

import { Formik, Form } from 'formik';
import { css } from 'styled-components/macro';

import api from 'api';
import { addNotification } from 'apps/core/flash_notifications/actions';
import { Alert, Button, Card, Radio } from 'bs';
import { Input } from 'components/FormikFields';
import { SimpleFetcher } from 'components/Fetcher';
import FormattedDateTime from 'components/FormattedDateTime';
import SubmitButton from 'components/SubmitButton';

const mfaFormCss = css`
    .form-group label, .form-check-label {
        background: transparent;
        font-weight: lighter;
    }
`;


const UserTOTPDevice = ({ fetchUser, user }) => {
    const device = user.totp_device;
    const [adding, setAdding] = useState(!device.verified_at);

    const deleteDevice = () => {
        api.delete(device.links.self).then(() => {
            fetchUser();
            setAdding(true);
        });
    };

    if (!adding) {
        return (
            <Card border="info" flavor="info" className='m-3 p-3'>
                <h4 className='mb-0'>{device.name}</h4>
                <small>Added: <FormattedDateTime date={device.verified_at} /></small>
                <hr />
                <Button flavor='danger' onClick={deleteDevice}>Delete</Button>
            </Card>
        );
    }

    const submitCode = (data, actions, onComplete=null) => {
        const { setSubmitting, setStatus } = actions;
        const url = device.links.verify_url;

        setSubmitting(true);
        api.post(url, data)
            .then(data => {
                setSubmitting(false);
                setAdding(false);
                fetchUser();
            })
            .catch(error => {
                setSubmitting(false);
                setStatus('Error validating code');
            });
    };


    return (
        <Card border="info" flavor="info" className='m-3 p-3'>
            <Formik
                initialValues={{ name: '', code: '' }}
                onSubmit={(values, actions) => {
                    submitCode(
                        values,
                        actions,
                        // onComplete
                    );
                }}
                validate={values => {
                    const code = (values.code || '').replace(' ', '');
                    if (code.length !== 6) {
                        return { code: 'Code must be six digits' };
                    }
                }}
            >
                {({ isSubmitting, isValid, status }) => (
                    <Form css={mfaFormCss}>
                        <QRCodeSVG
                            value={device.links.provisioning_uri}
                            style={{ display: 'block', margin: '0 auto' }}
                        />

                        {status && (
                            <Alert flavor='warning' className='mt-3'>
                                {status}
                            </Alert>
                        )}

                        <Input
                            name='name'
                            autoComplete='off'
                            label='Device name'
                            helpText='To help you identify this device. i.e. "My Phone"'
                        />
                        <Input
                            name='code'
                            label='Code'
                            helpText='Scan the QR code with your Authenticator app and type the code it gives you here.'
                            inputMode='numeric'
                            pattern='[0-9]*'
                            autoComplete='one-time-code'
                        />

                        <SubmitButton
                            disabled={!isValid}
                            submitting={isSubmitting}
                            label="Verify Code"
                        />
                    </Form>
                )}
            </Formik>
        </Card>
    );
};

const MFASettingsForm = ({ config, fetchUser, user }) => {
    const dispatch = useDispatch();

    const [valid, setValid] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [setting, setSetting] = useState(user.mfa_setting);

    const mfaEnabled = user.mfa_setting !== "disabled";
    const mfaOptional = config.choices.filter(i => i.name === 'disabled').length > 0;

    const submitSettings = () => {
        if (!valid) {
            return null;
        }

        const url = user.links.mfa_setting;
        const data = {
            mfa_setting: setting
        };

        setSubmitting(true);
        api.post(url, data)
            .then(data => {
                setSubmitting(false);
                setError(null);
                fetchUser();
                dispatch(addNotification({
                    message: "Saved!", flavor: "success"
                }));
            })
            .catch(error => {
                setSubmitting(false);
                setError(error);
                dispatch(addNotification({
                    message: "There was a problem.", flavor: "danger"
                }));
            });
    };

    const settingChanged = e => {
        const setting = e.target.value;
        setSetting(setting);

        if (setting === "disabled") {
            setValid(true);
        } else if (setting === "email") {
            setValid(true);
        } else {
            setValid(user.totp_device.verified_at !== null);
        }
    };

    return (
        <div css={mfaFormCss}>
            <Alert flavor={mfaEnabled ? 'success': 'warning'}>
                <h4>Keeping Your Account Safe</h4>
                <p>
                    When you login with your username and password, you
                    {mfaOptional ? " can optionally " : " will "}
                    be asked to confirm that login with a one-time code.
                </p>

                {mfaOptional && <p>We highly recommend enabling this feature.</p>}
            </Alert>

            {config.choices.map(choice => (
                <Radio
                    key={choice.name}
                    name='mfa_setting'
                    inline={false}
                    value={choice.name}
                    label={choice.label}
                    checked={choice.name === setting}
                    onChange={settingChanged}
                />
            ))}

            {setting === "totp" && (
                <UserTOTPDevice user={user} fetchUser={fetchUser} />
            )}

            <hr />

            <SubmitButton
                submitting={submitting}
                onClick={submitSettings}
                disabled={!valid}
                label='Save'
                icon='check-circle'
            />
        </div>
    );
};


const MFASettings = ({ user, fetchUser }) => {
    return (
        <SimpleFetcher
            url={user.links.mfa_setting}
            method='options'
            render={({ responseData }) => {
                return <MFASettingsForm user={user} fetchUser={fetchUser} config={responseData} />;
            }}
        />
    );
};

export default MFASettings;
